import React, { useCallback, useMemo } from "react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Edit, MoreHorizontal, Clock, ToggleRight, User as UserIcon } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";

import { DataTable, DataTableColumn } from "@/components/ui/DataTable";
import { User } from "@/types";
import { getResidencyLabel, getResidencyColorClasses } from '@/utils/residencyUtils';
import { StatusBadge, getStatusColor } from "@/utils/statusUtils";

// Import Employee type from useEmployeeManagement
type Employee = {
  id: string;
  name: string;
  email: string;
  role: string;
  department: string;
  position: string;
  status: string;
  shift: string;
  residency: string;
  workerType?: string;
  employee_id?: string;
  is_duty_roster?: boolean;
  group_id?: string;
  group_name?: string;
};

interface EmployeeTableProps {
  // ✅ FIXED: Restore proper User[] type for type safety
  data: User[] | Employee[];
  isLoading: boolean;
  handleDepartmentChange: (employeeId: string, newDepartment: string) => void;
  viewEmployeeProfile: (employee: User | Employee) => void;
  handleEditUser: (employee: User | Employee) => void;
  viewOvertimeHistory: (employee: User | Employee) => void;
  openChangeStatusDialog: (employee: User | Employee) => void;
  isReadOnly?: boolean; // Hide action buttons when true
  // ✅ NEW: Selection functionality for duty roster employees
  onEmployeeSelect?: (employeeId: string, isSelected: boolean) => void;
  selectedEmployees?: string[];
  showSelection?: boolean;
  // ✅ NEW: Groups tab specific configuration
  isGroupsTab?: boolean; // Show Group column instead of Duty Roster and Actions
}

export const EmployeeTable = React.memo(function EmployeeTable({
  // ✅ NEW: Use props instead of hook
  data: employees = [],
  isLoading,
  viewEmployeeProfile,
  handleEditUser,
  viewOvertimeHistory,
  openChangeStatusDialog,
  isReadOnly = false,
  // ✅ NEW: Selection functionality
  onEmployeeSelect,
  selectedEmployees = [],
  showSelection = false,
  // ✅ NEW: Groups tab configuration
  isGroupsTab = false
}: EmployeeTableProps) {
  // ✅ REMOVED: useAllEmployees hook - now using props
  // const { data: employees = [], isLoading, refetch } = useAllEmployees({ enabled: true });

  const getInitials = useCallback((name: string): string => {
    if (!name) return '?';
    const names = name.trim().split(' ');
    if (names.length === 1) return names[0].charAt(0).toUpperCase();
    return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
  }, []);


  const columns: DataTableColumn<any>[] = useMemo(() => [
    // ✅ NEW: Selection column for duty roster employees
    ...(showSelection ? [{
      key: 'select' as keyof (User | Employee),
      header: '',
      sortable: false,
      searchable: false,
      className: 'w-12',
      render: (_, row) => (
        <div className="flex justify-center">
          <input
            type="checkbox"
            checked={selectedEmployees.includes(row.id)}
            onChange={(e) => onEmployeeSelect?.(row.id, e.target.checked)}
            className="rounded border-gray-300"
          />
        </div>
      )
    }] : []),
    {
      key: 'name',
      header: 'Employee',
      sortable: true,
      searchable: true,
      className: 'text-left',
      render: (value, row) => (
        <div className="flex items-center space-x-3">
          <Avatar className="h-10 w-10 flex-shrink-0 ring-2 ring-background border-2 border-border">
            <AvatarFallback className="bg-gradient-to-br from-blue-100 to-blue-200 text-blue-700 font-semibold">
              {getInitials(String(value || ''))}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col min-w-0">
            <span className="font-semibold text-foreground truncate" title={String(value || '')}>
              {String(value || '')}
            </span>
            <span className="text-sm text-muted-foreground truncate">
              ID: {(row as Employee).employee_id || `EMP-${row.id.slice(0, 8)}`}
            </span>
          </div>
        </div>
      )
    },
    {
      key: 'employee_id',
      header: 'Employee ID',
      sortable: true,
      searchable: true,
      hidden: true, // Hide column from UI but keep it searchable
      render: (value, row) => (row as Employee).employee_id || `EMP-${row.id.slice(0, 8)}`
    },
    {
      key: 'department',
      header: 'Department',
      sortable: true,
      searchable: true,
      render: (value) => (
        <div className="text-center">
          <Badge variant="outline" className={`whitespace-normal ${getStatusColor('neutral', 'light')}`}>
            {String(value || "All Departments")}
          </Badge>
        </div>
      )
    },
    {
      key: 'position',
      header: 'Position',
      sortable: true,
      searchable: true,
      render: (value) => (
        <div className="text-center font-medium text-sm">
          {String(value || '-')}
        </div>
      )
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      searchable: true,
      render: (value) => {
        return (
          <div className="text-center">
            <StatusBadge 
              status={String(value || '')}
              className="font-medium"
            />
          </div>
        );
      }
    },
    {
      key: 'residency',
      header: 'Residency',
      sortable: true,
      searchable: true,
      render: (value) => {
        const residencyLabel = getResidencyLabel(String(value || ''));
        const colorClasses = getResidencyColorClasses(String(value || ''));
        
        return (
          <div className="text-center">
            <Badge 
              variant="outline" 
              className={`whitespace-normal ${colorClasses}`}
            >
              {residencyLabel}
            </Badge>
          </div>
        );
      }
    },
    // Show Group column for Groups tab, Duty Roster column for other tabs
    ...(isGroupsTab ? [{
      key: 'group_name',
      header: 'Group',
      sortable: true,
      searchable: true,
      render: (value) => (
        <div className="text-center">
          {value ? (
            <Badge variant="default" className="bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-950/20 dark:text-purple-300 dark:border-purple-800">
              {String(value)}
            </Badge>
          ) : (
            <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-950/20 dark:text-gray-300 dark:border-gray-800">
              No Group
            </Badge>
          )}
        </div>
      )
    }] : [{
      key: 'is_duty_roster',
      header: 'Duty Roster',
      sortable: true,
      searchable: true,
      render: (value, row) => (
        <div className="text-center">
          {value ? (
            <div className="flex flex-col items-center space-y-1">
              <Badge variant="default" className="bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-950/20 dark:text-blue-300 dark:border-blue-800">
                Duty Roster
              </Badge>
              {row.group_name && (
                <span className="text-xs text-muted-foreground">
                  {row.group_name}
                </span>
              )}
            </div>
          ) : (
            <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-950/20 dark:text-gray-300 dark:border-gray-800">
              Regular Shift
            </Badge>
          )}
        </div>
      )
    }]),
    // Only show Actions column if not in read-only mode and not in Groups tab
    ...(isReadOnly || isGroupsTab ? [] : [{
      key: 'id',
      header: 'Actions',
      sortable: false,
      render: (_, row) => (
        <div className="flex justify-center">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[160px]">
              <DropdownMenuItem onClick={() => viewEmployeeProfile(row)} className="cursor-pointer">
                <UserIcon className="mr-2 h-4 w-4 text-blue-600" />
                View Profile
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleEditUser(row)} className="cursor-pointer">
                <Edit className="mr-2 h-4 w-4 text-green-600" />
                Edit Employee
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => viewOvertimeHistory(row)} className="cursor-pointer">
                <Clock className="mr-2 h-4 w-4 text-yellow-600" />
                Overtime History
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => openChangeStatusDialog(row)} className="cursor-pointer">
                <ToggleRight className="mr-2 h-4 w-4 text-gray-500" />
                Change Status
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )
    }])
  ], [getInitials, viewEmployeeProfile, handleEditUser, viewOvertimeHistory, openChangeStatusDialog, isReadOnly, showSelection, selectedEmployees, onEmployeeSelect, isGroupsTab]);

  return (
    <div className="rounded-lg border bg-card">
      <DataTable
        data={employees}
        columns={columns}
        title="System Employees"
        isLoading={isLoading}
        config={{
          searchPlaceholder: "Search by name, employee ID, department, or position...",
          defaultSortField: 'name',
          defaultSortDirection: 'asc',
          rowsPerPage: 10,
          showSearch: true,
          showColumnVisibility: true,
          showExport: true,
          showRefresh: true,
          emptyStateMessage: "No employees found",
          noSearchResultsMessage: "No employees match your search criteria",
          exportFilename: 'employees'
        }}
        // ✅ REMOVED: onRefresh with refetch - no longer needed
        // onRefresh={async () => { await refetch(); return; }}
      />
    </div>
  );
});
